import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, TrendingUp, Activity, Zap, BarChart3, MessageSquare, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Landing = () => {
  const navigate = useNavigate();
  const [scores, setScores] = useState({});
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authMode, setAuthMode] = useState('signin');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authBusy, setAuthBusy] = useState(false);

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return 'Something went wrong. Please try again.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
    if (detail && typeof detail.msg === 'string') return detail.msg;
    return String(detail);
  };

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    setAuthBusy(true);
    try {
      const endpoint = authMode === 'signin' ? 'login' : 'register';
      await axios.post(`${API}/auth/${endpoint}`, { email: authEmail, password: authPassword }, { withCredentials: true });
      navigate('/dashboard');
    } catch (err) {
      setAuthError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setAuthBusy(false);
    }
  };

  useEffect(() => {
    // Check if user is logged in
    const checkAuth = async () => {
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        setIsLoggedIn(true);
      } catch {
        setIsLoggedIn(false);
      }
    };

    // Fetch live scores
    const fetchScores = async () => {
      try {
        const response = await axios.get(`${API}/sports/scores`);
        setScores(response.data || {});
      } catch (error) {
        console.error('Error fetching scores:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
    fetchScores();
  }, []);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const getSportColor = (sport) => {
    const colors = {
      mlb: 'text-red-400',
      nfl: 'text-brand-lime',
      wnba: 'text-orange-400'
    };
    return colors[sport] || 'text-brand-cyan';
  };

  return (
    <div className="min-h-screen bg-[#0a1628]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-[#1e3a5f]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_04125061-709a-438d-a302-3baa0ffd2bcb/artifacts/3susxjnb_IMG_1638.png" 
                alt="Free Props" 
                className="h-10 w-10 logo-glow"
              />
              <span className="font-heading text-2xl font-bold text-white tracking-tight">
                FREE PROPS
              </span>
            </div>
            <div className="flex items-center gap-4">
              {isLoggedIn ? (
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="bg-brand-cyan text-[#0a1628] hover:bg-brand-cyan/90 font-semibold btn-primary-glow"
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button 
                  onClick={handleGoogleLogin}
                  className="bg-brand-cyan text-[#0a1628] hover:bg-brand-cyan/90 font-semibold btn-primary-glow"
                  data-testid="get-started-btn"
                >
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(0,168,232,0.15)_0%,transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(140,198,63,0.1)_0%,transparent_50%)]" />
        
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#112240] border border-[#1e3a5f]">
                <Activity className="h-4 w-4 text-brand-lime" />
                <span className="text-sm text-muted-foreground">AI-Powered Sports Analytics</span>
              </div>
              
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white uppercase tracking-tight leading-none">
                SMARTER <span className="text-brand-cyan">PROPS.</span><br />
                BETTER <span className="text-brand-lime">BETS.</span>
              </h1>
              
              <p className="text-lg text-muted-foreground max-w-lg">
                Get AI-driven insights on MLB, NFL and WNBA player props with advanced 
                analytics and live scores. Compare lines and find the best value instantly.
              </p>

              {!isLoggedIn && (
                <div className="bg-[#0f1e36] border border-[#1e3a5f] rounded-lg p-5 max-w-md" data-testid="email-auth-card">
                  <div className="flex gap-2 mb-4">
                    {['signin', 'signup'].map(m => (
                      <button key={m} onClick={() => { setAuthMode(m); setAuthError(''); }}
                        className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${authMode === m ? 'bg-brand-cyan text-[#0a1628] border-brand-cyan' : 'border-[#1e3a5f] text-muted-foreground hover:text-white'}`}
                        data-testid={`auth-mode-${m}`}>
                        {m === 'signin' ? 'Sign In' : 'Create Account'}
                      </button>
                    ))}
                  </div>
                  <form onSubmit={handleEmailAuth} className="space-y-3">
                    <input type="email" required placeholder="Email address" value={authEmail} onChange={e => setAuthEmail(e.target.value)}
                      className="w-full bg-[#0a1628] border border-[#1e3a5f] rounded-md px-3 py-2 text-sm text-white placeholder:text-muted-foreground focus:outline-none focus:border-brand-cyan"
                      data-testid="auth-email-input" />
                    <input type="password" required placeholder="Password (6+ characters)" value={authPassword} onChange={e => setAuthPassword(e.target.value)}
                      className="w-full bg-[#0a1628] border border-[#1e3a5f] rounded-md px-3 py-2 text-sm text-white placeholder:text-muted-foreground focus:outline-none focus:border-brand-cyan"
                      data-testid="auth-password-input" />
                    {authError && <p className="text-red-400 text-xs" data-testid="auth-error">{authError}</p>}
                    <Button type="submit" disabled={authBusy} className="w-full bg-brand-cyan text-[#0a1628] hover:bg-brand-cyan/90 font-bold uppercase tracking-wider" data-testid="auth-submit-btn">
                      {authBusy ? 'Please wait...' : authMode === 'signin' ? 'Sign In' : 'Create Account'}
                    </Button>
                  </form>
                  <div className="flex items-center gap-3 my-3">
                    <div className="h-px bg-[#1e3a5f] flex-1" /><span className="text-[10px] uppercase text-muted-foreground">or</span><div className="h-px bg-[#1e3a5f] flex-1" />
                  </div>
                  <Button onClick={handleGoogleLogin} variant="outline" className="w-full border-[#1e3a5f] text-white hover:bg-[#112240] font-semibold" data-testid="google-login-hero-btn">
                    Continue with Google
                  </Button>
                </div>
              )}
            </div>
            
            {/* Live Scores Preview */}
            <div className="space-y-4">
              <div 
                className="flex items-center justify-between mb-6"
                data-testid="landing-live-scores-header"
              >
                <h3 className="font-heading text-xl font-bold text-white uppercase tracking-wide flex items-center gap-2">
                  Live Scores
                </h3>
                <span className="flex items-center gap-2 text-brand-lime text-sm">
                  <span className="w-2 h-2 bg-brand-lime rounded-full live-pulse" />
                  Live
                </span>
              </div>
              
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-24 skeleton rounded-md" />
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {['mlb', 'wnba', 'nfl']
                    .flatMap(s => (scores[s]?.events || []).map(e => ({ ...e, sport: s })))
                    .sort((a, b) => {
                      const rank = (e) => (e.status?.type?.state === 'in' ? 0 : e.status?.type?.state === 'pre' ? 1 : 2);
                      return rank(a) - rank(b);
                    })
                    .slice(0, 6)
                    .map((event, idx) => (
                      <Card 
                        key={`${event.sport}-${idx}`}
                        className="bg-[#0f1e36] border-[#1e3a5f] card-hover animate-slide-up"
                        style={{ animationDelay: `${idx * 0.08}s` }}
                        data-testid={`landing-score-card-${idx}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="space-y-1.5 flex-1">
                              {event.competitions?.[0]?.competitors?.map((team, tidx) => (
                                <div key={tidx} className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    {team.team?.logo && (
                                      <img src={team.team.logo} alt={team.team.abbreviation} className="w-5 h-5" />
                                    )}
                                    <span className={`text-sm font-semibold ${team.winner ? 'text-white' : 'text-muted-foreground'}`}>
                                      {team.team?.abbreviation || team.team?.shortDisplayName}
                                    </span>
                                  </div>
                                  <span className="font-mono font-bold text-white">{team.score || '0'}</span>
                                </div>
                              ))}
                            </div>
                            <div className="ml-5 text-right">
                              <span className={`sport-icon sport-${event.sport === 'wnba' ? 'nba' : event.sport} text-[9px] px-1.5 py-0.5`}>
                                {event.sport.toUpperCase()}
                              </span>
                              <div className={`text-[11px] uppercase tracking-wide mt-1.5 ${
                                event.status?.type?.state === 'in' 
                                  ? 'text-brand-lime' 
                                  : event.status?.type?.state === 'post'
                                  ? 'text-muted-foreground'
                                  : 'text-brand-cyan'
                              }`}>
                                {event.status?.type?.shortDetail || 'Scheduled'}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-[#0f1e36]/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white uppercase tracking-tight mb-4">
              YOUR EDGE IN <span className="text-brand-cyan">SPORTS BETTING</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Powerful tools and AI-driven insights to make smarter betting decisions
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: MessageSquare,
                title: 'Daily Matchup Edges',
                description: 'Pitcher vs batter breakdowns with advanced metrics and career history for every game.',
                color: 'text-brand-cyan'
              },
              {
                icon: BarChart3,
                title: 'Live Scores',
                description: 'Real-time MLB, NFL and WNBA scoreboards with spreads, totals and moneylines.',
                color: 'text-brand-lime'
              },
              {
                icon: TrendingUp,
                title: 'HR Zone',
                description: 'Daily home run projections for every game, batter, pitcher, and lineup on the slate.',
                color: 'text-orange-400'
              }
            ].map((feature, idx) => (
              <Card 
                key={idx} 
                className="bg-[#0f1e36] border-[#1e3a5f] card-hover stat-card"
                data-testid={`feature-card-${idx}`}
              >
                <CardContent className="p-8">
                  <feature.icon className={`h-12 w-12 ${feature.color} mb-6`} />
                  <h3 className="font-heading text-xl font-bold text-white uppercase tracking-wide mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Sports Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading text-3xl font-bold text-white uppercase tracking-tight mb-12 text-center">
            SUPPORTED LEAGUES
          </h2>
          
          <div className="grid grid-cols-3 gap-6 max-w-3xl mx-auto">
            {[
              { name: 'MLB', icon: '⚾', color: 'border-red-400' },
              { name: 'NFL', icon: '🏈', color: 'border-brand-lime' },
              { name: 'WNBA', icon: '🏀', color: 'border-orange-400' }
            ].map((sport, idx) => (
              <Card 
                key={idx}
                className={`bg-[#0f1e36] border-[#1e3a5f] hover:${sport.color} card-hover text-center`}
                data-testid={`sport-card-${sport.name.toLowerCase()}`}
              >
                <CardContent className="p-8">
                  <div className="text-4xl mb-4">{sport.icon}</div>
                  <h3 className="font-heading text-2xl font-bold text-white uppercase">
                    {sport.name}
                  </h3>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,168,232,0.2)_0%,transparent_70%)]" />
        
        <div className="max-w-3xl mx-auto text-center relative">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white uppercase tracking-tight mb-6">
            READY TO GET YOUR <span className="text-brand-lime">EDGE</span>?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Join thousands of bettors using AI-powered insights to find winning props.
          </p>
          <Button 
            onClick={isLoggedIn ? () => navigate('/dashboard') : handleGoogleLogin}
            size="lg"
            className="bg-brand-lime text-[#0a1628] hover:bg-brand-lime/90 font-bold uppercase tracking-wider text-lg px-8"
            data-testid="cta-btn"
          >
            Start Free
            <ChevronRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-[#1e3a5f]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_04125061-709a-438d-a302-3baa0ffd2bcb/artifacts/3susxjnb_IMG_1638.png" 
              alt="Free Props" 
              className="h-8 w-8"
            />
            <span className="font-heading text-lg font-bold text-white">FREE PROPS</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2025 Free Props. Gambling involves risk. Please bet responsibly.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
